import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import type { FC, PropsWithChildren } from 'react';
import { useState } from 'react';
import { Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import type { InferType } from 'yup';
import { boolean, object, string } from 'yup';

import useForm from '@/hooks/useForm';
import { queries } from '@/queries';
import type { ApplicationWebhook } from '@/services/applicationWebhooks';
import {
  deleteApplicationWebhook,
  patchApplicationWebhook,
  postApplicationWebhook,
  rotateApplicationWebhookSecret,
  testApplicationWebhook,
} from '@/services/applicationWebhooks';
import type { DjangoData } from '@/services/common';
import { dateFormatter } from '@/utils/date';

import Alert from '../Alert';
import Button from '../Button';
import Label from '../Form/Fields/Label';
import TextInputField from '../Form/Fields/TextInputField';
import Text from '../Text';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from '../ui/dialog';
import { Switch } from '../ui/switch';

const ApplicationWebhooks = () => {
  const { data } = useQuery({
    ...queries.applicationWebhooks.list,
  });

  return (
    <div className="flex flex-1 flex-col gap-4">
      <Text variant="body-small" className="text-neutral-800">
        MindPal sends a signed POST request to these URLs for every new
        application.
      </Text>
      <div className="flex-1">
        {data?.results.map((webhook) => (
          <ApplicationWebhookElement key={webhook.id} {...webhook} />
        ))}
      </div>
      <WebhookFormDialog>
        <Button className="self-end">Add webhook</Button>
      </WebhookFormDialog>
    </div>
  );
};

export default ApplicationWebhooks;

const ApplicationWebhookElement: FC<ApplicationWebhook> = (webhook) => {
  const { id, name, url, isActive, createdAt } = webhook;
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);

  const { mutate: toggleActive } = useMutation({
    mutationFn: patchApplicationWebhook,
    mutationKey: ['toggleApplicationWebhook'],
    onMutate: ({ isActive: newIsActive }) => {
      queryClient.setQueryData<DjangoData<ApplicationWebhook>>(
        queries.applicationWebhooks.list.queryKey,
        (prev) =>
          prev && {
            ...prev,
            results: prev.results.map((item) =>
              item.id === id ? { ...item, isActive: !!newIsActive } : item
            ),
          }
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries(queries.applicationWebhooks.list);
    },
  });

  const { mutate: remove, isLoading: isDeleting } = useMutation({
    mutationFn: () => deleteApplicationWebhook(id),
    mutationKey: ['deleteApplicationWebhook'],
    onSuccess: () => {
      queryClient.invalidateQueries(queries.applicationWebhooks.list);
    },
  });

  const { mutate: rotateSecret, isLoading: isRotating } = useMutation({
    mutationFn: () => rotateApplicationWebhookSecret(id),
    mutationKey: ['rotateApplicationWebhookSecret'],
    onSuccess: (response) => setSecret(response.secret),
  });

  const { mutate: sendTest, isLoading: isTesting } = useMutation({
    mutationFn: () => testApplicationWebhook(id),
    mutationKey: ['testApplicationWebhook'],
    onSuccess: ({ success, statusCode }) => {
      if (success) toast.success(`Delivered (${statusCode})`);
      else toast.error(`Failed (${statusCode ?? 'timeout'})`);
    },
  });

  return (
    <div className="flex items-center justify-between gap-4 border-b py-2 text-black">
      <div className="min-w-0">
        <Text className="font-semibold">{name}</Text>
        <Text variant="body-caption" className="truncate text-neutral-800">
          {url}
        </Text>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Text variant="body-caption" className="text-neutral-800">
          Created:{' '}
          {dateFormatter({
            date: createdAt,
            format: { dateStyle: 'medium' },
          })}
        </Text>
        <Switch
          size="sm"
          checked={isActive}
          onCheckedChange={(checked) => toggleActive({ id, isActive: checked })}
        />
        <Button
          size="sm"
          variant="secondary"
          onClick={() => sendTest()}
          isLoading={isTesting}
        >
          Send test
        </Button>
        <WebhookFormDialog webhook={webhook}>
          <Button size="sm" variant="secondary">
            Edit
          </Button>
        </WebhookFormDialog>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => rotateSecret()}
          isLoading={isRotating}
          confirm="Are you sure you want to rotate the secret? The old secret stops working immediately, so the receiver has to be updated with the new one"
        >
          Rotate secret
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className="!border-error font-semibold text-error"
          onClick={() => remove()}
          isLoading={isDeleting}
          confirm="Are you sure you want to delete this webhook? This action cannot be undone"
        >
          Delete
        </Button>
      </div>
      <Dialog
        open={!!secret}
        onOpenChange={(open) => {
          if (!open) setSecret(null);
        }}
      >
        <DialogContent onInteractOutside={(event) => event.preventDefault()}>
          <DialogHeader>New signing secret</DialogHeader>
          {secret && <WebhookSecret secret={secret} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

type WebhookFormDialogProps = {
  webhook?: ApplicationWebhook;
};
const WebhookFormDialog: FC<PropsWithChildren<WebhookFormDialogProps>> = ({
  children,
  webhook,
}) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const getDefaultValues = () => ({
    name: webhook?.name ?? '',
    url: webhook?.url ?? '',
    isActive: webhook?.isActive ?? true,
  });

  const {
    register,
    control,
    handleSubmit,
    setError,
    reset: resetForm,
    formState: { errors },
  } = useForm<InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(),
  });

  const onError = (err: unknown) => {
    const detail =
      err instanceof AxiosError ? err.response?.data?.detail : undefined;
    if (typeof detail === 'string' && detail.startsWith('url:'))
      return setError('url', { message: detail.replace('url:', '').trim() });
    if (typeof detail === 'string' && detail.startsWith('name:'))
      return setError('name', { message: detail.replace('name:', '').trim() });
    return toast.error(detail ?? 'Something went wrong');
  };

  const onSuccess = () => {
    queryClient.invalidateQueries(queries.applicationWebhooks.list);
  };

  const create = useMutation({
    mutationFn: postApplicationWebhook,
    mutationKey: ['createApplicationWebhook'],
    meta: { silent: true },
    onSuccess,
    onError,
  });

  const update = useMutation({
    mutationFn: patchApplicationWebhook,
    mutationKey: ['updateApplicationWebhook'],
    meta: { silent: true },
    onSuccess: () => {
      onSuccess();
      setOpen(false);
    },
    onError,
  });

  const onOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) return resetForm(getDefaultValues());
    return create.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        onInteractOutside={(event) => {
          if (create.data) event.preventDefault();
        }}
      >
        {create.data ? (
          <>
            <DialogHeader>Webhook created</DialogHeader>
            <WebhookSecret secret={create.data.secret} />
          </>
        ) : (
          <>
            <DialogHeader>
              {webhook ? 'Edit webhook' : 'New webhook'}
            </DialogHeader>
            <form
              onSubmit={handleSubmit((formData) =>
                webhook
                  ? update.mutate({ id: webhook.id, ...formData })
                  : create.mutate(formData)
              )}
              className="flex flex-col gap-2"
            >
              <TextInputField
                containerClassName="!gap-2"
                variant="transparentNeutralBorder"
                labelClassName="text-neutral-800"
                label="Name"
                placeholder="n8n – new applications"
                className="text-black"
                error={errors.name?.message}
                {...register('name')}
              />
              <TextInputField
                containerClassName="!gap-2"
                variant="transparentNeutralBorder"
                labelClassName="text-neutral-800"
                label="URL"
                placeholder="https://"
                className="text-black"
                error={errors.url?.message}
                {...register('url')}
              />
              <Controller
                control={control}
                name="isActive"
                render={({ field: { value, onChange } }) => (
                  <div className="flex items-center justify-between py-2">
                    <Label className="text-neutral-800">Active</Label>
                    <Switch checked={value} onCheckedChange={onChange} />
                  </div>
                )}
              />
              <Button
                isLoading={create.isLoading || update.isLoading}
                type="submit"
              >
                {webhook ? 'Save' : 'Create'}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

type WebhookSecretProps = {
  secret: string;
};
const WebhookSecret: FC<WebhookSecretProps> = ({ secret }) => {
  const onCopy = () => {
    try {
      navigator.clipboard.writeText(secret);
      toast.success('Copied to clipboard');
    } catch (_e) {
      toast.error('Something went wrong');
    }
  };

  return (
    <>
      <Alert
        label="Make sure to copy your signing secret now. You won’t be able to see it again! Paste it into the receiver (e.g. n8n credentials) to verify requests."
        variant="critical"
      />
      <div>
        <Label className="text-neutral-800">Signing secret</Label>
        <div className="relative">
          <TextInputField
            variant="transparentNeutralBorder"
            readOnly
            className="text-black"
            value={secret}
            canHaveErrorMessage={false}
          />
          <Button
            onClick={onCopy}
            className="!absolute right-2 top-1/2 -translate-y-1/2"
          >
            <FontAwesomeIcon icon={faCopy} />
          </Button>
        </div>
      </div>
      <DialogClose asChild>
        <Button>I&apos;ve copied it</Button>
      </DialogClose>
    </>
  );
};

const schema = object({
  name: string().trim().required('Name is required').max(128),
  url: string()
    .trim()
    .required('URL is required')
    .url('Enter a valid URL')
    .max(512)
    .test('https', 'URL must start with https://', (value) =>
      value.startsWith('https://')
    ),
  isActive: boolean().required(),
});
